import React from 'react';
import styled from 'styled-components';
import { lineHeight, theme, toRem } from 'design';
import CheckBG from './image/checkbox-bg.svg';

interface ICheckboxProps {
  label?: any;
  id: string;
  name?: string;
  size?: 'large' | 'small';
  onChange?: any;
  checked?: boolean;
  className?: string;
  disabled?: boolean;
}

const SIZE_LARGE = toRem(22);
const SIZE_SMALL = toRem(16.5);

const Layout = styled.div<Partial<ICheckboxProps>>`
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;

  input {
    width: ${({ size }) => (size === 'small' ? SIZE_SMALL : SIZE_LARGE)};
    height: ${({ size }) => (size === 'small' ? SIZE_SMALL : SIZE_LARGE)};
  }
`;

const Input = styled.input<Partial<ICheckboxProps>>`
  appearance: none;
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  margin-right: ${({ size }) => (size === 'small' ? toRem(theme.space.md) : toRem(theme.space.xl))};
  border-radius: ${toRem(2.75)};
  border: ${toRem(1)} solid ${theme.color.neutral[300]};
  outline: none;
  background-color: ${theme.color.white};
  cursor: pointer;

  &:hover {
    background-color: ${theme.color.white};

    &:disabled {
      cursor: not-allowed;
    }
  }

  &:disabled {
    background-color: transparent;
  }

  &:focus {
    outline: none;
    border-color: ${theme.color.neutral[400]};
  }

  :checked {
    border-color: ${theme.color.secondary[400]};
    background-color: ${theme.color.secondary[400]};
    background-image: url(${CheckBG});
    background-repeat: no-repeat;
    background-position: center center;
    background-size: 100% 100%;
  }
`;

const Label = styled.label<Partial<ICheckboxProps>>`
  display: flex;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(theme.font.size.sm)};
  line-height: ${lineHeight(theme.font.size.sm)};
  color: ${theme.color.neutral[800]};
  align-items: center;
  cursor: pointer;

  &.disabled:hover {
    cursor: not-allowed;
  }

  a {
    display: inline;
    text-decoration: underline;
    color: inherit;
  }
`;

const Checkbox: React.FC<ICheckboxProps> = ({ label, id, name, size = 'large', onChange, checked, className, disabled, ...rest }) => (
  <Layout className={className} size={size}>
    <Label className={disabled && 'disabled'}>
      <Input type="checkbox" id={id} name={name || id} checked={checked} onChange={onChange} disabled={disabled} {...rest} />
      {label}
    </Label>
  </Layout>
);

export default Checkbox;
